/**
 * NovaSuggestionCard — bridges a NovaSuggestion (output of the rules
 * engine) into the existing NovaCard UI. Hides the rules-engine shape
 * from page code so consumers can just say:
 *
 *   const { top, dismiss } = useNovaSuggestions();
 *   {top && <NovaSuggestionCard suggestion={top} onDismiss={dismiss} />}
 */

import { useNavigate } from 'react-router-dom';
import { NovaCard } from './NovaCard';
import type { NovaSuggestion } from '@/types/novaSuggestion';

export interface NovaSuggestionCardProps {
  suggestion: NovaSuggestion;
  /** Called with the suggestion id when the user dismisses ("Not now"). */
  onDismiss?: (id: string) => void;
  /** Called when the user clicks the primary action (after navigation). */
  onAct?: (s: NovaSuggestion) => void;
  className?: string;
}

export const NovaSuggestionCard = ({
  suggestion,
  onDismiss,
  onAct,
  className,
}: NovaSuggestionCardProps) => {
  const navigate = useNavigate();

  // Compose the message from title + body so the card stays single-message.
  // NovaCard wraps the body so a longer combined string still reads.
  const message = `${suggestion.title}. ${suggestion.body}`;

  const primary = {
    label: suggestion.action?.label ?? 'Open it',
    onClick: () => {
      if (suggestion.action?.href) {
        navigate(suggestion.action.href);
      }
      onAct?.(suggestion);
    },
  };

  const secondary = {
    label: suggestion.action?.dismissLabel ?? 'Not now',
    onClick: () => onDismiss?.(suggestion.id),
  };

  return (
    <NovaCard
      message={message}
      primaryAction={primary}
      secondaryAction={secondary}
      novaState={suggestion.novaState ?? 'has-suggestion'}
      className={className}
    />
  );
};

export default NovaSuggestionCard;
